<template>
    <div class="block-metrics-chart">
        <div class="chart-controls mb-3">
            <div class="row">
                <div class="col-md-8">
                    <h6>Metrics to Display:</h6>
                    <div class="form-check-inline">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="rewards-toggle"
                            v-model="visibleMetrics.rewards"
                            @change="updateChart"
                        >
                        <label class="form-check-label" for="rewards-toggle">
                            Rewards
                        </label>
                    </div>
                    <div class="form-check-inline">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="tips-toggle"
                            v-model="visibleMetrics.tips"
                            @change="updateChart"
                        >
                        <label class="form-check-label" for="tips-toggle">
                            Jito Tips
                        </label>
                    </div>
                    <div class="form-check-inline">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="duration-toggle"
                            v-model="visibleMetrics.blockDuration"
                            @change="updateChart"
                        >
                        <label class="form-check-label" for="duration-toggle">
                            Block Duration
                        </label>
                    </div>
                    <div class="form-check-inline">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="cu-toggle"
                            v-model="visibleMetrics.computeUnits"
                            @change="updateChart"
                        >
                        <label class="form-check-label" for="cu-toggle">
                            Compute Units
                        </label>
                    </div>
                    <div class="form-check-inline">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="txns-toggle"
                            v-model="visibleMetrics.transactions"
                            @change="updateChart"
                        >
                        <label class="form-check-label" for="txns-toggle">
                            Total Transactions
                        </label>
                    </div>
                    <div class="form-check-inline">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="nonvotes-toggle"
                            v-model="visibleMetrics.nonVotes"
                            @change="updateChart"
                        >
                        <label class="form-check-label" for="nonvotes-toggle">
                            Non-Vote Transactions
                        </label>
                    </div>
                </div>
                <div class="col-md-4 text-end">
                    <button
                        @click="refreshData"
                        class="btn btn-outline-primary btn-sm"
                        :disabled="loading"
                    >
                        <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
                        Refresh
                    </button>
                </div>
            </div>
        </div>

        <div class="chart-container" style="position: relative; height: 400px;">
            <canvas ref="chartCanvas"></canvas>
        </div>

        <div v-if="loading" class="text-center p-3">
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            Loading chart data...
        </div>

        <div v-if="error" class="alert alert-warning mt-2">
            {{ error }}
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

export default {
    name: 'BlockMetricsChart',
    props: {
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            chart: null,
            chartData: [],
            loading: false,
            error: null,
            visibleMetrics: {
                rewards: true,
                tips: true,
                blockDuration: false,
                computeUnits: false,
                transactions: false,
                nonVotes: false
            },
            colors: {
                rewards: {
                    background: 'rgba(153, 69, 255, 0.2)',
                    border: '#9945ff'
                },
                tips: {
                    background: 'rgba(96, 138, 255, 0.2)',
                    border: '#608aff'
                },
                blockDuration: {
                    background: 'rgba(92, 183, 255, 0.2)',
                    border: '#5cb7ff'
                },
                computeUnits: {
                    background: 'rgba(154, 218, 255, 0.2)',
                    border: '#9adaff'
                },
                transactions: {
                    background: 'rgba(255, 94, 71, 0.2)',
                    border: '#ff5e47'
                },
                nonVotes: {
                    background: 'rgba(255, 131, 41, 0.2)',
                    border: '#ff8329'
                }
            }
        }
    },
    mounted() {
        this.initChart()
        this.fetchData()
    },
    beforeUnmount() {
        if (this.chart) {
            this.chart.destroy()
        }
    },
    watch: {
        filters: {
            handler() {
                this.fetchData()
            },
            deep: true
        }
    },
    methods: {
        initChart() {
            const ctx = this.$refs.chartCanvas.getContext('2d')

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets: []
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        tooltip: {
                            callbacks: {
                                title: (context) => {
                                    const point = context[0]
                                    const dataPoint = this.chartData[point.dataIndex]
                                    if (dataPoint) {
                                        return `Slot: ${dataPoint.slot} (${dataPoint.granularity})`
                                    }
                                    return ''
                                },
                                label: (context) => {
                                    const label = context.dataset.label
                                    let value = context.parsed.y

                                    if (label.includes('SOL')) {
                                        value = (value / 1000000000).toFixed(6) + ' SOL'
                                    } else if (label.includes('CU')) {
                                        value = (value / 1000000).toFixed(2) + 'M CU'
                                    } else if (label.includes('Duration')) {
                                        value = (value / 1000000).toFixed(2) + ' ms'
                                    } else {
                                        value = Math.round(value).toLocaleString()
                                    }

                                    return `${label}: ${value}`
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            position: 'bottom',
                            title: {
                                display: true,
                                text: 'Slot'
                            },
                            ticks: {
                                callback: function(value) {
                                    return Math.round(value).toLocaleString()
                                }
                            }
                        },
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Value'
                            }
                        }
                    },
                    elements: {
                        point: {
                            radius: 2,
                            hoverRadius: 4
                        },
                        line: {
                            tension: 0.1
                        }
                    }
                }
            })
        },

        async fetchData() {
            this.loading = true
            this.error = null

            try {
                const params = new URLSearchParams()

                // Add filters to params
                Object.keys(this.filters).forEach(key => {
                    if (this.filters[key] !== null && this.filters[key] !== undefined) {
                        params.append(`filter[${key}]`, this.filters[key])
                    }
                })

                const response = await axios.get(`/blocks/chart-data?${params.toString()}`)
                this.chartData = response.data
                this.updateChart()
            } catch (error) {
                console.error('Error fetching chart data:', error)
                this.error = 'Failed to load chart data'
            } finally {
                this.loading = false
            }
        },

        updateChart() {
            if (!this.chart || !this.chartData.length) return

            const datasets = []

            // Map reactive chartData to simple objects
            const validData = this.chartData.map(d => ({
                slot: d.slot,
                granularity: d.granularity,
                rewards: d.rewards || 0,
                jito_total: d.jito_total || 0,
                block_duration: d.block_duration || 0,
                compute_units: d.compute_units || 0,
                transactions: d.transactions || 0,
                non_votes: d.non_votes || 0,
                date: new Date(d.timestamp)
            })).filter(d => d.slot !== null && !isNaN(d.slot))
console.log(validData)
            if (this.visibleMetrics.rewards && validData.some(d => d.rewards > 0)) {
                datasets.push({
                    label: 'Validator Rewards (SOL)',
                    data: validData.map(d => ({ x: d.slot, y: d.rewards })),
                    backgroundColor: this.colors.rewards.background,
                    borderColor: this.colors.rewards.border,
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y'
                })
            }

            if (this.visibleMetrics.tips && validData.some(d => d.jito_total > 0)) {
                datasets.push({
                    label: 'Jito Tips (SOL)',
                    data: validData.map(d => ({ x: d.slot, y: d.jito_total })),
                    backgroundColor: this.colors.tips.background,
                    borderColor: this.colors.tips.border,
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y'
                })
            }

            if (this.visibleMetrics.blockDuration && validData.some(d => d.block_duration > 0)) {
                datasets.push({
                    label: 'Block Duration (ms)',
                    data: validData.map(d => ({ x: d.slot, y: d.block_duration })),
                    backgroundColor: this.colors.blockDuration.background,
                    borderColor: this.colors.blockDuration.border,
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y'
                })
            }

            if (this.visibleMetrics.computeUnits && validData.some(d => d.cu > 0)) {
                datasets.push({
                    label: 'Compute Units',
                    data: validData.map(d => ({ x: d.slot, y: d.cu })),
                    backgroundColor: this.colors.computeUnits.background,
                    borderColor: this.colors.computeUnits.border,
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y'
                })
            }

            if (this.visibleMetrics.transactions && validData.some(d => d.txns > 0)) {
                datasets.push({
                    label: 'Total Transactions',
                    data: validData.map(d => ({ x: d.slot, y: d.txns })),
                    backgroundColor: this.colors.transactions.background,
                    borderColor: this.colors.transactions.border,
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y'
                })
            }

            if (this.visibleMetrics.nonVotes && validData.some(d => d.non_votes > 0)) {
                datasets.push({
                    label: 'Non-Vote Transactions',
                    data: validData.map(d => ({ x: d.slot, y: d.non_votes })),
                    backgroundColor: this.colors.nonVotes.background,
                    borderColor: this.colors.nonVotes.border,
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y'
                })
            }

            console.log(this.chart, this.chart.value)
            this.chart.data.datasets = datasets
            // this.chart.update('none')
        },

        refreshData() {
            this.fetchData()
        }
    }
}
</script>

<style scoped>
.block-metrics-chart {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-controls {
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1rem;
}

.form-check-inline {
    margin-right: 1rem;
    margin-bottom: 0.5rem;
}

.chart-container {
    margin-top: 1rem;
}
</style>
